/**
 * Screenshot-Aufnahme per Screen-Capture-API (F4, PRD-Entscheidung 5).
 *
 * Δ Ninox: PERSISTENTE Tab-Verbindung. Der Melder gibt den Ninox-Tab EINMAL
 * frei (Browser-Sicherheit erzwingt diese eine Auswahl); danach bleibt der
 * Stream offen. Jeder weitere Screenshot ist ein sofortiger Frame-Abgriff —
 * ohne erneuten Dialog und ohne Tab-Wechsel (ein geteilter Hintergrund-Tab
 * wird weiter erfasst). Eine Live-Vorschau des Streams dient als Auslöser.
 */

/**
 * Laengste Kante des Uploads: Retina-/5K-Aufnahmen erzeugen sonst PNGs
 * ueber dem Upload-Limit (Backend 10 MB, nginx 12 MB) — Chrome meldet das
 * bei HTTP/2 nur als "Network Error". 2560px reicht fuer Review + KI.
 */
const MAX_KANTE = 2560

function zielGroesse(breite: number, hoehe: number): [number, number] {
  const laengste = Math.max(breite, hoehe)
  if (laengste <= MAX_KANTE) return [breite, hoehe]
  const faktor = MAX_KANTE / laengste
  return [Math.round(breite * faktor), Math.round(hoehe * faktor)]
}

/**
 * Persistente Verbindung zum freigegebenen Ninox-Tab. Einmal `verbinden()`
 * (nativer Freigabe-Dialog), dann beliebig oft `frameAbgreifen()`.
 */
export class NinoxTabCapture {
  private stream: MediaStream | null = null
  private track: MediaStreamTrack | null = null
  private video: HTMLVideoElement | null = null
  /** Wird gerufen, wenn der Nutzer das Teilen beendet / den Tab schließt. */
  onEnded: (() => void) | null = null

  get verbunden(): boolean {
    return this.track?.readyState === "live"
  }

  /** Der Live-Stream — zum Anzeigen einer Vorschau in einem <video>. */
  get mediaStream(): MediaStream | null {
    return this.stream
  }

  /** Einmalige Tab-Freigabe; hält den Stream danach offen. */
  async verbinden(): Promise<void> {
    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: { displaySurface: "browser" },
      audio: false,
      // KEIN preferCurrentTab — der Melder waehlt den Ninox-Tab (Fremd-Tab).
    } as DisplayMediaStreamOptions)

    const track = stream.getVideoTracks()[0]
    const video = document.createElement("video")
    video.srcObject = stream
    video.muted = true
    await video.play()
    if (!video.videoWidth) {
      await new Promise<void>((r) => {
        video.onloadedmetadata = () => r()
      })
    }

    track.addEventListener("ended", () => {
      this.aufraeumen()
      this.onEnded?.()
    })

    this.stream = stream
    this.track = track
    this.video = video
  }

  /** Sofortiger Einzelbild-Abgriff aus dem laufenden Stream (kein Dialog). */
  async frameAbgreifen(): Promise<Blob> {
    if (!this.verbunden || !this.video) {
      throw new Error("Ninox-Tab ist nicht verbunden")
    }
    const video = this.video
    // Einen Frame abwarten, damit das Bild aktuell ist
    await new Promise((r) => requestAnimationFrame(r))
    const vb = video.videoWidth || 1280
    const vh = video.videoHeight || 720

    const canvas = document.createElement("canvas")
    ;[canvas.width, canvas.height] = zielGroesse(vb, vh)
    const ctx = canvas.getContext("2d")
    if (!ctx) throw new Error("Canvas-Kontext nicht verfügbar")
    ctx.drawImage(video, 0, 0, vb, vh, 0, 0, canvas.width, canvas.height)

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("Screenshot fehlgeschlagen"))),
        "image/png"
      )
    })
  }

  /** Verbindung beenden (Stream stoppen). */
  trennen(): void {
    this.aufraeumen()
  }

  private aufraeumen(): void {
    this.stream?.getTracks().forEach((t) => t.stop())
    this.stream = null
    this.track = null
    this.video = null
  }
}

/** Fallback (PRD §8 „Einbettung"): Screenshot als Datei hochladen —
 * ebenfalls auf MAX_KANTE begrenzen, kleine Bilder bleiben unveraendert. */
export async function dateiAlsBlob(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file).catch(() => null)
  if (!bitmap) return file
  const [breite, hoehe] = zielGroesse(bitmap.width, bitmap.height)
  if (breite === bitmap.width && hoehe === bitmap.height) {
    bitmap.close()
    return file
  }
  const canvas = document.createElement("canvas")
  canvas.width = breite
  canvas.height = hoehe
  canvas.getContext("2d")!.drawImage(bitmap, 0, 0, breite, hoehe)
  bitmap.close()
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Bild nicht lesbar"))),
      "image/png"
    )
  })
}
